import React, { useEffect, useState, Fragment } from 'react';
import { Check, ChevronDown, Sparkles, Calendar, Mail } from 'lucide-react';
import { PageHeader } from '../components/page/PageHeader';
import { Reveal } from '../components/ui/Reveal';
import { Button } from '../components/ui/Button';
import { MarketplaceModal } from '../components/MarketplaceModal';
import { HowMarketplaceWorks } from '../components/page/HowMarketplaceWorks';
interface Tier {
  name: string;
  desc: string;
  monthly: string;
  annual: string;
  note: string;
  cta: string;
  features: string[];
  highlight: boolean;
}
const tiers: Tier[] = [{
  name: 'Casting',
  desc: 'Post casting calls and receive custom submissions',
  monthly: '$149',
  annual: '$125',
  note: '40% off Beta pricing',
  cta: 'Try Casting',
  features: ['30-day free trial', 'Post unlimited casting calls', 'Access a network of 400+ talent agencies', 'Receive talent submissions & quotes from talent agencies', 'Live creator metrics & audience demographic data', 'Direct talent manager contact details', 'Search & filter submissions', '3 users'],
  highlight: false
}, {
  name: 'Marketplace',
  desc: 'Search and discover represented talent',
  monthly: '$239',
  annual: '$199',
  note: '40% off Beta pricing',
  cta: 'Request Marketplace Access',
  features: ['Approval required', '30-day free trial', 'Everything in Casting', 'Search 6,500 represented creators', 'Browse 400+ talent agency rosters', 'Marketplace insights & pricing benchmarks', 'Consulting sessions', 'Unlimited users', 'Priority customer support'],
  highlight: true
}];
const faqs = [{
  q: 'How does the free trial work?',
  a: 'Both Casting and Marketplace plans come with a 30-day free trial so you can explore the platform and post your first casting call risk-free.'
}, {
  q: 'Why does Marketplace require approval?',
  a: 'The roster database and talent agency directory contain sensitive contact and pricing information. We vet users to ensure a high-quality environment for our talent agencies.'
}, {
  q: 'Can I switch plans later?',
  a: 'Anytime. Plans are monthly or annual, and you can upgrade or downgrade whenever your needs change.'
}, {
  q: 'Who owns the creator data?',
  a: 'Talent agencies own and manage their creators’ profiles. Brands and agencies always see live, agency-maintained data that stays current.'
}];
const stats = ['400+ Talent Agencies', '6,500+ Represented Creators', '$500K+ GMV Facilitated'];
const SEO = {
  title: 'Pricing for Brands and Marketing Agencies | Live Talent Agency Roster Database & Casting Calls | Zano',
  description: 'Compare Zano plans for brands and marketing agencies. Access live talent agency rosters, discover represented creators, connect with talent managers, and post unlimited casting calls.',
  keywords: 'influencer marketing pricing, talent agency roster database, represented creators, influencer casting calls, talent agency directory, creator discovery platform',
  ogTitle: 'Pricing | Zano Influencer Marketplace',
  ogDescription: "Explore pricing for brands and marketing agencies using Zano's marketplace for represented creators."
};
function updateMetaTag(attribute: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attribute}="${key}"]`;
  const existingTag = document.head.querySelector<HTMLMetaElement>(selector);
  const tag = existingTag ?? document.createElement('meta');
  const previousContent = existingTag?.getAttribute('content') ?? null;
  if (!existingTag) {
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
  return () => {
    if (!existingTag) {
      tag.remove();
      return;
    }
    if (previousContent === null) {
      existingTag.removeAttribute('content');
    } else {
      existingTag.setAttribute('content', previousContent);
    }
  };
}
const FaqItem = ({
  q,
  a
}: {
  q: string;
  a: string;
}) => {
  const [open, setOpen] = useState(false);
  return <div className="border-b border-gray-200 py-5" data-id="element-2709" data-mp="2ebpnbmqii77v">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left gap-4" data-id="element-2710" data-mp="2t3rqbfnvpgc4">
        <span className="font-semibold text-gray-900" data-id="element-2711" data-mp="2o9wy1nu7jt1f">{q}</span>
        <ChevronDown size={20} className={`text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-2712" data-mp="322d30u6o614z" />
      </button>
      {open && <p className="mt-3 text-gray-600 leading-relaxed" data-id="element-2713" data-mp="3c1auo5ze6as7">{a}</p>}
    </div>;
};
export const Pricing = () => {
  const [annual, setAnnual] = useState(false);
  const [isMarketplaceModalOpen, setIsMarketplaceModalOpen] = useState(false);
  useEffect(() => {
    const previousTitle = document.title;
    document.title = SEO.title;
    const restoreDescription = updateMetaTag('name', 'description', SEO.description);
    const restoreKeywords = updateMetaTag('name', 'keywords', SEO.keywords);
    const restoreOgTitle = updateMetaTag('property', 'og:title', SEO.ogTitle);
    const restoreOgDescription = updateMetaTag('property', 'og:description', SEO.ogDescription);
    return () => {
      document.title = previousTitle;
      restoreDescription();
      restoreKeywords();
      restoreOgTitle();
      restoreOgDescription();
    };
  }, []);
  return <>
      <MarketplaceModal isOpen={isMarketplaceModalOpen} onClose={() => setIsMarketplaceModalOpen(false)} data-id="element-2714" data-mp="35k0xyxmztdhq" />
      <PageHeader eyebrow="For Brands & Marketing Agencies" title="Get direct access to hundreds of talent agencies" subtitle="Discover talent, post casting calls and receive curated submissions directly from talent agencies." data-id="element-2715" data-mp="2vn4nimdjahaj" />

      {/* Stats + beta banner */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 mb-10 text-center" data-id="element-2716" data-mp="yuulpc7zk5jt">
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm font-medium text-gray-500" data-id="element-2717" data-mp="ysi97ekzyne5">
          {stats.map((s, i) => <Fragment key={s} data-id="element-2718" data-mp="sdkuuki9q2h9" data-mpi={i}>
              <span data-id="element-2719" data-mp="sgp2jruxzgco">{s}</span>
              {i < stats.length - 1 && <span className="text-gray-300" aria-hidden="true" data-id="element-2720" data-mp="ngau48pfi4e4">
                  |
                </span>}
            </Fragment>)}
        </div>
        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-zano-lavender/40 border border-zano-pink/20 px-4 py-2 text-sm" data-id="element-2721" data-mp="1g4geuvghftdy">
          <Sparkles size={16} className="text-zano-pink" data-id="element-2722" data-mp="1b5vp5pyuws3r" />
          <span className="text-gray-700" data-id="element-2723" data-mp="16dlslr38ktsi">
            Beta Pricing —{' '}
            <span className="font-semibold text-zano-pink" data-id="element-2724" data-mp="11falho4bsxdz">
              Lock in 40% off
            </span>{' '}
            for life
          </span>
        </div>
      </div>

      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3 mb-12" data-id="element-2725" data-mp="1u47ybbprq1zc">
        <span className={`text-sm font-semibold ${annual ? 'text-zano-charcoal' : 'text-gray-400'}`} data-id="element-2726" data-mp="1u1v9cellercs">
          Annual{' '}
          <span className={annual ? 'text-zano-pink' : 'text-gray-400'} data-id="element-2727" data-mp="26dfi99kxg9tv">
            (save 20%)
          </span>
        </span>
        <button onClick={() => setAnnual(!annual)} aria-label="Toggle annual billing" className="relative w-12 h-6 rounded-full bg-zano-pink/20 transition-colors" data-id="element-2728" data-mp="2bcikyndfq3yo">
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-zano-pink shadow transition-transform ${!annual ? 'translate-x-6' : ''}`} data-id="element-2729" data-mp="2layf6z7rdtce" />
        </button>
        <span className={`text-sm font-semibold ${!annual ? 'text-zano-charcoal' : 'text-gray-400'}`} data-id="element-2730" data-mp="2u530z481cel2">
          Monthly
        </span>
      </div>

      {/* Tiers */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-2731" data-mp="3430o1oe5s5c3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch" data-id="element-2732" data-mp="390j0vpvar4pd">
          {tiers.map((t, i) => <Reveal key={t.name} delay={i * 0.08} className="h-full" data-id="element-2733" data-mp="3ub7rrsx9rdwr" data-mpi={i}>
              <div className={`relative h-full flex flex-col rounded-2xl border p-7 ${t.highlight ? 'border-zano-pink shadow-float bg-white' : 'border-gray-200 bg-white shadow-sm'}`} data-id="element-2734" data-mp="3pcwipkest7fl">
                {t.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-zano-pink text-white text-xs font-semibold px-3 py-1 rounded-full" data-id="element-2735" data-mp="28q3i0tzvz0c">
                    Most Popular
                  </span>}
                <h3 className="text-lg font-bold text-gray-900" data-id="element-2736" data-mp="36kbuiga1nqi4">{t.name}</h3>
                <p className={`text-sm text-gray-500 mb-5 ${t.name === 'Casting' ? 'md:whitespace-nowrap' : ''}`} data-id="element-2737" data-mp="3kfe87pl4uk5h">
                  {t.desc}
                </p>
                <div className="mb-1 flex items-end gap-1" data-id="element-2738" data-mp="2rvxsu8od6835">
                  <span className="text-4xl font-bold text-zano-charcoal" data-id="element-2739" data-mp="2myyzhsciy560">
                    {annual ? t.annual : t.monthly}
                  </span>
                  <span className="text-sm text-gray-500 mb-1" data-id="element-2740" data-mp="12f3o4gs6t89m">
                    {annual ? '/ month, billed annually' : '/ month'}
                  </span>
                </div>
                <p className="text-sm font-semibold text-zano-pink mb-6" data-id="element-2741" data-mp="tgzyp9e2zrcj">
                  {t.note}
                </p>
                {t.name === 'Marketplace' ? <Button variant={t.highlight ? 'primary' : 'secondary'} className="w-full justify-center" onClick={() => setIsMarketplaceModalOpen(true)} data-id="element-2742" data-mp="1m3ulicydf2t6">
                    {t.cta}
                  </Button> : <a href="https://app.zanoplatform.com/get-started?tier=explorer&type=brand&cycle=monthly" className="block" data-id="element-2743" data-mp="1h485xwv2lah7">
                    <Button variant={t.highlight ? 'primary' : 'secondary'} className="w-full justify-center" data-id="element-2744" data-mp="5qnn9ojmnch7">
                      {t.cta}
                    </Button>
                  </a>}
                <ul className="mt-7 space-y-3" data-id="element-2745" data-mp="ahyejp0actsi">
                  {t.features.map(f => <li key={f} className="flex items-start gap-2.5 text-sm text-gray-700" data-id="element-2746" data-mp="ag410vrcg3yg">
                      <Check size={16} className="text-zano-pink mt-0.5 shrink-0" data-id="element-2747" data-mp="1gh34jxtpga5" />{' '}
                      {f}
                    </li>)}
                </ul>
              </div>
            </Reveal>)}
        </div>
      </section>

      <HowMarketplaceWorks audience="brands" background="white" data-id="element-2748" data-mp="3spnvvmo389hh" />

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-2749" data-mp="1lxwrw52hy1zj">
        <Reveal data-id="element-2750" data-mp="1qv5d6eixym50">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8" data-id="element-2751" data-mp="1qvolr4mpby6d">
            Frequently asked questions
          </h2>
          <div data-id="element-2752" data-mp="36fecvl33maup">
            {faqs.map(f => <FaqItem key={f.q} q={f.q} a={f.a} data-id="element-2753" data-mp="36gpgnkwjnilt" />)}
          </div>
        </Reveal>
      </section>

      {/* Bottom CTA */}
      <section className="py-20" data-id="element-2754" data-mp="3l5vaetcm4gmb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2755" data-mp="3gd26g7oyxcba">
          <Reveal data-id="element-2756" data-mp="3u4qk4fev88ud">
            <div className="bg-zano-charcoal rounded-3xl p-10 md:p-16 text-center text-white relative overflow-hidden" data-id="element-2757" data-mp="3u47bjpb3uwt0">
              <div className="absolute top-0 right-0 w-64 h-64 bg-zano-pink/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-2758" data-mp="3p4m2un64va33" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-zano-lavender/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-2759" data-mp="7vfu78lkb110" />
              <div className="relative z-10 max-w-2xl mx-auto" data-id="element-2760" data-mp="29odhkowkl1ek">
                <h2 className="text-3xl md:text-4xl font-bold mb-5" data-id="element-2761" data-mp="29qq8hrkb2m3r">
                  Not sure which plan fits?
                </h2>
                <p className="text-lg text-gray-300 mb-8" data-id="element-2762" data-mp="2ueg3ndjqvuq2">
                  Book a demo with us, or ask us anything by emailing{' '}
                  <a href="mailto:support@zanoplatform.com" className="text-white font-semibold underline underline-offset-2 hover:text-zano-pink transition-colors" data-id="element-2763" data-mp="2yg1jn7rksp4h">
                    support@zanoplatform.com
                  </a>
                  .
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2764" data-mp="3dazce1qmzgm3">
                  <a href="mailto:support@zanoplatform.com?subject=Book%20a%20demo%20with%20Zano" className="w-full sm:w-auto" data-id="element-2765" data-mp="3hc131u6hmkcg">
                    <Button variant="primary" className="bg-white text-zano-charcoal hover:bg-gray-100 w-full sm:w-auto px-8 py-3" data-id="element-2766" data-mp="3r6b7wbcm9b42">
                      <Calendar size={18} className="mr-1.5" data-id="element-2767" data-mp="4uw6a0f69zzv" />
                      Book a Demo
                    </Button>
                  </a>
                  <a href="mailto:support@zanoplatform.com" className="w-full sm:w-auto" data-id="element-2768" data-mp="1kfexiz00w3xd">
                    <Button variant="secondary" className="bg-transparent text-white border-gray-600 hover:bg-gray-800 hover:border-gray-500 w-full sm:w-auto px-8 py-3" data-id="element-2769" data-mp="1fh3kygicffbg">
                      <Mail size={18} className="mr-1.5" data-id="element-2770" data-mp="236pn9cu0iano" />
                      Email Support
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>;
};